import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { STATUSES, PIPELINE, FWD_STATUSES, TERM_STATUSES, OUTCOMES, STATUS_STYLES } from "../lib/statusConfig";
import { useColors } from "../context/ColorContext";
import { StatusBadge, DisciplineTag } from "../components/StatusBadge";
import { GrievanceForm } from "../components/GrievanceForm";
import { GrievanceAttachments } from "../components/GrievanceAttachments";
import { BUCKETS, bucketLabel } from "../lib/buckets";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Check, Pencil, Trash2, Building2, User, Calendar, Shield, Paperclip, IdCard, MessageSquare, Send, Phone, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatDate, formatDateTime } from "../lib/dates";

function e164Preview(phone) {
  if (!phone) return null;
  const d = (phone.match(/\d/g) || []).join("");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return phone.startsWith("+") ? phone : null;
}

export default function GrievanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const { statusStyle } = useColors();
  const [g, setG] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsBody, setSmsBody] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsConfig, setSmsConfig] = useState(null);
  const [smsLog, setSmsLog] = useState([]);
  const [editHist, setEditHist] = useState(null);
  const [histStatus, setHistStatus] = useState("");
  const [histNote, setHistNote] = useState("");
  const [specialist, setSpecialist] = useState("");
  const [specialistOptions, setSpecialistOptions] = useState([]);

  const load = useCallback(() => {
    api.get(`/grievances/${id}`).then((r) => { setG(r.data); setNewStatus(r.data.status); setSpecialist(r.data.specialist || ""); });
  }, [id]);

  useEffect(() => {
    api.get("/options").then((r) => setSpecialistOptions(r.data.specialists || [])).catch(() => setSpecialistOptions([]));
  }, []);

  useEffect(() => {
    if (!g?.grievant_name) return;
    api.get("/agent-options", { params: { name: g.grievant_name, division: g.division || "ramp" } })
      .then((r) => setAgentOptions(r.data || [])).catch(() => setAgentOptions([]));
  }, [g?.grievant_name, g?.division]);

  const assignEid = async (eid) => {
    try {
      await api.put(`/grievances/${id}`, { eid: eid === "__none__" ? "" : eid });
      toast.success(eid && eid !== "__none__" ? `Assigned to EID ${eid}` : "Unassigned");
      load();
    } catch (e) {
      toast.error("Failed to update assignment");
    }
  };

  useEffect(() => { load(); }, [load]);

  const startEditHist = (origIndex, h) => {
    setEditHist(origIndex);
    setHistStatus(h.status || "");
    setHistNote(h.note || "");
  };

  const saveHist = async (origIndex) => {
    if (!histStatus) { toast.error("Select a status"); return; }
    try {
      await api.put(`/grievances/${id}/history/${origIndex}`, { status: histStatus, note: histNote });
      toast.success("History entry updated");
      setEditHist(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update history");
    }
  };

  const deleteHist = async (origIndex) => {
    try {
      await api.delete(`/grievances/${id}/history/${origIndex}`);
      toast.success("History entry deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete history");
    }
  };

  const advance = async () => {
    const isSpecialistBucket = g.bucket === "fwd_office" || g.bucket === "terminations";
    const specialistChanged = isSpecialistBucket && specialist !== (g.specialist || "");
    if (newStatus === g.status && !note.trim() && !specialistChanged) {
      toast.error("Select a new status, add a note, or change the assignment");
      return;
    }
    setUpdating(true);
    try {
      const payload = { status: newStatus, note };
      if (isSpecialistBucket) payload.specialist = specialist;
      await api.put(`/grievances/${id}/status`, payload);
      setNote("");
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const remove = async () => {
    await api.delete(`/grievances/${id}`);
    toast.success("Grievance deleted");
    navigate(-1);
  };

  const moveToBucket = async (bucket) => {
    if (bucket === g.bucket) return;
    try {
      await api.post("/grievances/bulk-update", { ids: [id], bucket });
      toast.success(`Moved to ${bucketLabel(bucket)}`);
      load();
    } catch (e) {
      toast.error("Failed to move grievance");
    }
  };

  const smsAgent = agentOptions.find((a) => a.eid === g?.eid) || agentOptions[0] || null;
  const smsPhone = smsAgent ? e164Preview(smsAgent.phone) : null;

  const openSms = async () => {
    const tmpl = `TWU Local 555 BNA: Your grievance ${g.case_no} has been updated to "${g.status}". Reply STOP to opt out, HELP for help.`;
    setSmsBody(tmpl);
    setSmsOpen(true);
    try {
      const [cfg, lg] = await Promise.all([
        api.get("/sms/config"),
        api.get("/sms/messages", { params: { grievance_id: id } }),
      ]);
      setSmsConfig(cfg.data);
      setSmsLog(lg.data || []);
    } catch (e) { /* ignore */ }
  };

  const sendSms = async () => {
    if (!smsBody.trim() || !smsPhone) return;
    setSmsSending(true);
    try {
      await api.post("/sms/send", {
        to: smsAgent.phone, body: smsBody.trim(),
        grievance_id: id, grievant_name: g.grievant_name, eid: g.eid,
      });
      toast.success(`Text sent to ${g.grievant_name}`);
      const lg = await api.get("/sms/messages", { params: { grievance_id: id } });
      setSmsLog(lg.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send text");
    } finally {
      setSmsSending(false);
    }
  };

  if (!g) return <div className="p-8 text-muted-foreground text-sm">Loading case…</div>;

  const isOutcome = OUTCOMES.includes(g.status);
  const steps = g.bucket === "fwd_office" ? FWD_STATUSES : g.bucket === "terminations" ? TERM_STATUSES : PIPELINE;
  const currentIdx = isOutcome ? steps.length - 1 : steps.indexOf(g.status);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
      <button onClick={() => navigate(-1)} data-testid="back-button"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to grievances
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-xs bg-secondary px-2 py-1 rounded-sm">{g.case_no}</span>
            <StatusBadge status={g.status} size="md" />
            <DisciplineTag discipline={g.discipline} />
            {g.has_files && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Paperclip className="w-3 h-3" /> Files</span>
            )}
          </div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">{g.subject}</h1>
        </div>
        <div className="flex gap-2">
          {can("status") && (
            <Select value={g.bucket} onValueChange={moveToBucket}>
              <SelectTrigger data-testid="move-bucket" className="rounded-sm w-[190px] bg-card">
                <SelectValue placeholder="Move to board…" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {BUCKETS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isAdmin && (
            <Button variant="outline" className="rounded-sm" data-testid="text-grievant-button" onClick={openSms}>
              <MessageSquare className="w-4 h-4 mr-2" /> Text grievant
            </Button>
          )}
          {can("edit") && (
            <Button variant="outline" className="rounded-sm" data-testid="edit-button" onClick={() => setEditOpen(true)}>
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
          {can("delete") && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="delete-button"
                  className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/5">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm bg-card">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this grievance?</AlertDialogTitle>
                  <AlertDialogDescription>This will permanently remove case {g.case_no}.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={remove}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="border border-border bg-card rounded-sm p-6 mb-4 overflow-x-auto">
        <div className="flex items-center min-w-[820px]">
          {steps.map((s, i) => {
            const done = i < currentIdx || (isOutcome && i <= currentIdx);
            const active = !isOutcome && i === currentIdx;
            const style = statusStyle(s);
            return (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-2 shrink-0" style={{ width: 108 }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors"
                    style={{
                      backgroundColor: active ? style.dot : done ? "#18181B" : "#fff",
                      borderColor: active ? style.dot : done ? "#18181B" : "#E4E4E7",
                      color: active || done ? "#fff" : "#A1A1AA",
                    }}>
                    {done ? <Check className="w-4 h-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                  </div>
                  <span className={`text-[10px] text-center leading-tight ${active ? "font-bold text-foreground" : "text-muted-foreground"}`}>{s}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-0.5 -mt-5" style={{ backgroundColor: i < currentIdx || isOutcome ? "#18181B" : "#E4E4E7" }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {isOutcome && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Final outcome:</span>
            <StatusBadge status={g.status} size="md" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-border bg-card rounded-sm">
            <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Case Details</h2></div>
            <div className="p-5 grid grid-cols-2 gap-5 text-sm">
              <Field icon={User} label="Grievant" value={
                <button
                  data-testid="detail-grievant-link"
                  onClick={() => navigate(`/grievants/${encodeURIComponent(g.grievant_name)}?division=${g.division || 'ramp'}${g.eid ? `&eid=${encodeURIComponent(g.eid)}` : ""}`)}
                  className="hover:underline underline-offset-2 decoration-dotted text-left"
                >
                  {g.grievant_name}
                </button>
              } />
              <Field icon={Shield} label="Rep" value={g.rep || "—"} />
              <Field icon={Building2} label="Discipline" value={g.discipline || "N/A"} />
              <Field icon={Calendar} label="Submitted / Due" value={formatDate(g.date_due)} />
              <Field icon={IdCard} label="Employee No. (EID)" value={
                can("edit") && agentOptions.length > 0 ? (
                  <Select value={g.eid || "__none__"} onValueChange={assignEid}>
                    <SelectTrigger data-testid="assign-eid" className="rounded-sm h-8 w-full max-w-[220px] bg-card">
                      <SelectValue placeholder="Assign agent…" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {agentOptions.map((o) => (
                        <SelectItem key={o.eid} value={o.eid}>EID {o.eid} · Rank #{o.rank}{o.phone ? ` · ${o.phone}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (g.eid || "—")
              } />
            </div>
            {can("edit") && agentOptions.length > 1 && (
              <div className="px-5 pb-4 -mt-1">
                <p className="text-[11px] text-amber-600 dark:text-amber-500 flex items-center gap-1.5">
                  {agentOptions.length} agents share this name — assign the correct EID to attribute this case.
                </p>
              </div>
            )}
            {g.comments && (
              <div className="px-5 pb-5">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Comments</div>
                <p className="text-sm leading-relaxed">{g.comments}</p>
              </div>
            )}
          </div>

          {can("status") && (
            <div className="border border-border bg-card rounded-sm">
              <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Update Status</h2></div>
              <div className="p-5 space-y-3">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea data-testid="status-note" className="rounded-sm" placeholder="Add a note (optional)…"
                  value={note} onChange={(e) => setNote(e.target.value)} />
                {(g.bucket === "fwd_office" || g.bucket === "terminations") && (
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1 block">District Rep / Grievance Specialist</label>
                    <Select value={specialist || "__none__"} onValueChange={(v) => setSpecialist(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="specialist-select" className="rounded-sm bg-card">
                        <SelectValue placeholder="Assign specialist…" />
                      </SelectTrigger>
                      <SelectContent className="bg-card">
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {specialistOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {specialistOptions.length === 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">No specialists added yet. A Master Admin can add them in the Admin Portal.</p>
                    )}
                  </div>
                )}
                <Button data-testid="update-status-button" onClick={advance} disabled={updating}
                  className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90">
                  {updating ? "Updating…" : "Update Status"}
                </Button>
              </div>
            </div>
          )}

          <GrievanceAttachments gid={id} canEdit={can("edit")} />
        </div>

        <div className="border border-border bg-card rounded-sm">
          <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Case History</h2></div>
          <div className="p-5">
            <div className="space-y-0">
              {[...(g.history || [])].reverse().map((h, i, arr) => {
                const origIndex = arr.length - 1 - i;
                const isEditing = editHist === origIndex;
                return (
                <div key={`${h.at}-${h.status}-${i}`} className="flex gap-3 pb-4 last:pb-0" data-testid={`history-event-${i}`}>
                  <div className="flex flex-col items-center">
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5" style={{ backgroundColor: STATUS_STYLES[h.status]?.dot || "#A1A1AA" }} />
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-border my-1" />}
                  </div>
                  <div className="pb-1 flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-2" data-testid={`history-edit-form-${i}`}>
                        <Select value={histStatus} onValueChange={setHistStatus}>
                          <SelectTrigger className="rounded-sm h-8 w-full" data-testid="history-edit-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Textarea data-testid="history-edit-note" value={histNote} onChange={(e) => setHistNote(e.target.value)}
                          rows={2} placeholder="Note (optional)" className="rounded-sm text-sm" />
                        <div className="flex items-center gap-1">
                          <Button size="sm" className="h-8 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid={`history-save-${i}`} onClick={() => saveHist(origIndex)}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 rounded-sm" onClick={() => setEditHist(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="group flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{h.status}</div>
                          {h.note && <div className="text-xs text-muted-foreground mt-0.5">{h.note}</div>}
                          <div className="text-[11px] text-muted-foreground mt-1">
                            {formatDate(h.at)}{h.edited_at && <span className="italic"> · edited</span>}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm"
                              data-testid={`history-edit-${i}`} onClick={() => startEditHist(origIndex, h)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm text-destructive"
                                  data-testid={`history-delete-${i}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="rounded-sm bg-card">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this history entry?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Removes the “{h.status}” entry from this case's history. The grievance's current status is not changed.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                  <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90"
                                    data-testid={`history-delete-confirm-${i}`} onClick={() => deleteHist(origIndex)}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <GrievanceForm open={editOpen} onOpenChange={setEditOpen} existing={g} onSaved={load} division={g.division} />

      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="bg-card max-w-lg" data-testid="sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Text {g.grievant_name}</DialogTitle>
          </DialogHeader>
          {smsConfig && !smsConfig.configured && (
            <div className="flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400 px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4" /> SMS is not configured on the server.
            </div>
          )}
          <div className="text-sm flex items-center gap-2" data-testid="sms-dialog-phone">
            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
            {smsPhone
              ? <span>{smsAgent.phone} <span className="text-muted-foreground">→ {smsPhone}</span>{smsAgent.eid ? <span className="text-muted-foreground font-mono"> · EID {smsAgent.eid}</span> : null}</span>
              : <span className="text-red-600 dark:text-red-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> No valid phone on file for this grievant.</span>}
          </div>
          <textarea
            data-testid="sms-dialog-body"
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
            rows={5}
            maxLength={1600}
            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">{smsBody.length}/1600</span>
            <Button data-testid="sms-dialog-send" onClick={sendSms} disabled={smsSending || !smsBody.trim() || !smsPhone} className="rounded-sm">
              <Send className="w-4 h-4 mr-2" /> {smsSending ? "Sending…" : "Send Text"}
            </Button>
          </div>
          {smsLog.length > 0 && (
            <div className="mt-2 border-t border-border pt-3">
              <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Sent for this case</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {smsLog.map((m) => (
                  <div key={m.id} className="text-xs border border-border rounded-sm p-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{m.to}</span>
                      <span className="capitalize text-muted-foreground">{m.status}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
                    <div className="text-[10px] text-muted-foreground mt-1">{formatDateTime(m.sent_at)} · by {m.sent_by}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
